import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import msalInstance, { loginRequest } from "../utils/msalConfig";
import { initiateOutlookLogin, getOAuthError } from "../services/outlookOAuth";
import { getPostLoginPath } from "../config/featureFlags";

const ALLOWED_DOMAIN = "@scalecapacity.com";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Check for OAuth errors in URL params
  useEffect(() => {
    const oauthError = getOAuthError();
    if (oauthError) {
      setError(oauthError.errorDescription || oauthError.error);
      toast.error(oauthError.errorDescription || oauthError.error);
      // Clear URL params
      navigate("/login", { replace: true });
    }
  }, [searchParams, navigate]);

  // Reset loading state when user navigates back to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setLoading(false);
      }
    };

    const handlePageShow = (event) => {
      // Reset loading if page is loaded from cache (back/forward navigation)
      if (event.persisted) {
        setLoading(false);
      }
    };

    // Listen to visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    // Listen to pageshow event (fires when navigating back)
    window.addEventListener("pageshow", handlePageShow);

    // Also reset loading on mount
    setLoading(false);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const validateDomain = (email) => {
    if (!email.includes("@")) return false;
    const domain = email.split("@")[1];
    return domain.toLowerCase() === ALLOWED_DOMAIN.substring(1).toLowerCase();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Email is required");
      return;
    }

    if (!password) {
      setError("Password is required");
      return;
    }

    if (!validateDomain(email)) {
      setError(
        "Only ScaleCapacity organization members are allowed to access this platform."
      );
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);

      if (result.success) {
        const raw = localStorage.getItem("user");
        const u = raw ? JSON.parse(raw) : null;
        navigate(getPostLoginPath(u));
      } else {
        setError(result.error || "Login failed");
        toast.error(result.error || "Login failed");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="min-h-screen w-full flex">
      {/* Left Side - Guidance Content */}
      <motion.div
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary to-secondary relative overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Animated Background Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"
            animate={{
              x: [0, 100, 0],
              y: [0, 50, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"
            animate={{
              x: [0, -80, 0],
              y: [0, -60, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <motion.div variants={itemVariants}>
            <img
              src="/new_tablecloth.png"
              alt="HRMS Logo"
              className="w-48 h-auto mb-8"
            />
          </motion.div>

          <motion.h1
            className="text-5xl font-bold mb-6"
            variants={itemVariants}
          >
            Welcome to HRMS Platform
          </motion.h1>

          <motion.p
            className="text-xl text-white/90 mb-8 leading-relaxed"
            variants={itemVariants}
          >
            Streamline your human resources management with our comprehensive
            platform designed for modern enterprises.
          </motion.p>

          <motion.div className="space-y-6" variants={itemVariants}>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">
                  Employee Management
                </h3>
                <p className="text-white/80">
                  Manage employee records, profiles, and organizational
                  structure efficiently.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">
                  Attendance Tracking
                </h3>
                <p className="text-white/80">
                  Real-time attendance monitoring with location-based
                  check-in/check-out.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Leave Management</h3>
                <p className="text-white/80">
                  Streamlined leave requests, approvals, and holiday calendar
                  management.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Secure & Private</h3>
                <p className="text-white/80">
                  Enterprise-grade security with encrypted data and role-based
                  access control.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Full Login Form */}
      <div className="w-full lg:w-1/2 bg-gray-50 min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center px-8 lg:px-16 py-12">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-10">
              <h2 className="text-4xl font-bold text-primary mb-3">
                Welcome Back
              </h2>
              <p className="text-lg text-gray-600">
                Sign in to continue to HRMS Platform
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="your.email@scalecapacity.com"
                  className="w-full px-4 py-4 rounded-2xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-base"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your password"
                  className="w-full px-4 py-4 rounded-2xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-base"
                  required
                />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-2xl"
                >
                  <p className="text-sm text-red-600">{error}</p>
                </motion.div>
              )}

              {/* Sign In Button */}
              <motion.button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-4 bg-primary text-white rounded-2xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </motion.button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-50 text-gray-500">Or</span>
              </div>
            </div>
            

            {/* New Outlook OAuth Button (Server-side Flow) */}
            <motion.button
              type="button"
              onClick={() => {
                try {
                  setLoading(true);
                  setError("");
                  
                  // Clear all MSAL cookies and sessionStorage before initiating login
                  console.log("🧹 Clearing MSAL state and cookies...");
                  
                  // Clear sessionStorage
                  const msalKeys = Object.keys(sessionStorage).filter((key) =>
                    key.includes("msal") || key.includes("interaction")
                  );
                  msalKeys.forEach((key) => sessionStorage.removeItem(key));
                  
                  // Clear localStorage MSAL keys
                  const msalLocalKeys = Object.keys(localStorage).filter((key) =>
                    key.includes("msal")
                  );
                  msalLocalKeys.forEach((key) => localStorage.removeItem(key));
                  
                  // Clear cookies related to MSAL
                  document.cookie.split(";").forEach((cookie) => {
                    const eqPos = cookie.indexOf("=");
                    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                    if (name.includes("msal") || name.includes("interaction")) {
                      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
                    }
                  });
                  
                  console.log("✅ Cleared MSAL state");
                  console.log("🔄 Initiating Outlook OAuth login (new flow)...");
                  
                  // Small delay to ensure cookies are cleared
                  setTimeout(() => {
                    initiateOutlookLogin();
                  }, 100);
                } catch (err) {
                  console.error("❌ Outlook OAuth login error:", err);
                  setError("Failed to initiate Outlook login: " + (err.message || "Unknown error"));
                  toast.error("Failed to initiate Outlook login");
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="w-full mt-4 px-6 py-4 bg-white border-2 border-blue-500 text-blue-700 rounded-2xl font-semibold transition-all duration-300 hover:scale-105 hover:border-blue-600 hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              <svg
                className="w-6 h-6 shrink-0"
                viewBox="0 0 21 21"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect width="10" height="10" x="0" y="0" fill="#F25022" />
                <rect width="10" height="10" x="11" y="0" fill="#7FBA00" />
                <rect width="10" height="10" x="0" y="11" fill="#00A4EF" />
                <rect width="10" height="10" x="11" y="11" fill="#FFB900" />
              </svg>
              <span>Login with Microsoft</span>
            </motion.button>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-xs text-center text-gray-500">
                By continuing, you agree to our Terms of Service and Privacy
                Policy
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
